import React from "react";
import { Input } from "../ui/input";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import { Camera, X } from "lucide-react";

export const CONDITION_OPTIONS = [
    { value: "", label: "— Non renseigné —" },
    { value: "neuf", label: "Neuf" },
    { value: "bon", label: "Bon état" },
    { value: "correct", label: "Correct" },
    { value: "use", label: "Traces d'usure" },
    { value: "endommage", label: "Endommagé" },
];

export const CONDITION_LABELS = { neuf: "Neuf", bon: "Bon état", correct: "Correct", use: "Traces d'usure", endommage: "Endommagé" };

export const CONDITION_STYLES = {
    neuf: "bg-green-100 text-green-700",
    bon: "bg-emerald-100 text-emerald-700",
    correct: "bg-yellow-100 text-yellow-700",
    use: "bg-orange-100 text-orange-700",
    endommage: "bg-red-100 text-red-700",
};

export function ConditionBadge({ condition, testId }) {
    if (!condition) return null;
    return (
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${CONDITION_STYLES[condition] || "bg-gray-100 text-gray-600"}`} data-testid={testId}>
            {CONDITION_LABELS[condition] || condition}
        </span>
    );
}

export function HandoverSection({ value, onChange, photos, setPhotos, people = [], departments = [], mode = "remise" }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    const isRemise = mode === "remise";
    const toggleDateUndefined = (checked) => {
        onChange({ ...value, date_undefined: checked, date: checked ? "" : (value.date || new Date().toISOString().slice(0, 10)) });
    };
    return (
        <div className="border-t pt-3 mt-3" data-testid="handover-section">
            <p className="text-xs font-medium text-gray-600 mb-2">État des lieux {isRemise ? "à la remise" : "à la restitution"}</p>
            <div className="grid grid-cols-2 gap-3">
                <div><label className="form-label text-xs">État du matériel</label>
                    <select value={value.condition || ""} onChange={(e) => set("condition", e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="handover-condition">
                        {CONDITION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div><label className="form-label text-xs">{isRemise ? "Date de remise" : "Date de restitution"}</label>
                    <Input type="date" value={value.date || ""} onChange={(e) => set("date", e.target.value)} disabled={!!value.date_undefined} className={value.date_undefined ? "opacity-50" : ""} data-testid="handover-date" />
                    <label className="flex items-center gap-1.5 mt-1 text-[11px] text-gray-500 cursor-pointer select-none">
                        <input type="checkbox" checked={!!value.date_undefined} onChange={(e) => toggleDateUndefined(e.target.checked)} className="rounded border-gray-300 accent-teal-600" data-testid="handover-date-undefined" />
                        Date non définie
                    </label>
                </div>
            </div>
            {isRemise && (
                <div className="mt-2"><label className="form-label text-xs">Remis par</label>
                    <UserSearchSelect users={people} departments={departments} value={value.handed_over_by} onChange={(v) => set("handed_over_by", v)} placeholder="Par défaut : vous" data-testid="handover-by-select" />
                </div>
            )}
            {!isRemise && (
                <div className="mt-2"><label className="form-label text-xs">Matériel rendu par (optionnel)</label>
                    <UserSearchSelect users={people} departments={departments} value={value.returned_by} onChange={(v) => set("returned_by", v)} placeholder="Par défaut : l'employé assigné" data-testid="returned-by-select" />
                    <p className="text-[10px] text-gray-400 mt-0.5">Si une autre personne a rendu le matériel — recherche dans l'annuaire (MS365 et employés locaux)</p>
                </div>
            )}
            <div className="mt-2"><label className="form-label text-xs">Remarques sur l'état</label>
                <textarea value={value.condition_notes || ""} onChange={(e) => set("condition_notes", e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border rounded-md resize-none" placeholder="Rayure sur le capot, batterie neuve, chargeur fourni..." data-testid="handover-notes" />
            </div>
            <div className="mt-2">
                <label className="form-label text-xs">Photos (optionnel)</label>
                <input type="file" accept="image/*" multiple
                    onChange={(e) => { setPhotos([...photos, ...Array.from(e.target.files || [])]); e.target.value = ""; }}
                    className="block text-[11px] text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[11px] file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                    data-testid="handover-photos-input" />
                {photos.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {photos.map((f, i) => (
                            <span key={`${f.name}-${f.size}-${f.lastModified}`} className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-gray-100 text-gray-600" data-testid={`handover-photo-${i}`}>
                                <Camera className="w-3 h-3" />{f.name}
                                <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export const emptyHandover = (currentUserId) => ({
    condition: "",
    condition_notes: "",
    date: new Date().toISOString().slice(0, 10),
    date_undefined: false,
    handed_over_by: currentUserId || null,
    returned_by: null,
});

export async function uploadHandoverPhotos(apiModule, itemId, historyId, photos, label) {
    for (const f of photos) {
        await apiModule.uploadInventoryAttachment(itemId, f, label, { category: "etat_des_lieux", historyId });
    }
}
